import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react'
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState, ColumnOrderState, ColumnSizingState } from '@tanstack/react-table'
import { DragDropContext, Droppable, Draggable, DropResult, } from '@hello-pangea/dnd'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { formatDateWithDateAndMonthName, getValueFromResource } from '../../common/timeLiveFunction'
import AttendanceDetail from './AttendanceDetail'
import GeofenceModal from './GeofenceModal'
import { GetAccountEmployeeAttendanceListAsync, GetAccountEmployeeAttendanceViewDetails, GetAttendanceGeofenceLocationActivity } from './AttendanceSlice'
import moment from 'moment'
import { format12HoursOnly } from '../../common/UiUtilities'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Loader from '../../common/Loader'

interface AttendanceRecord {
    Id: number
    AccountId: number
    AccountEmployeeId: number
    Employeename: string
    EmployeeCode: string
    SourceName: string
    AttendanceDate: string
    FirstCheckIn: string | null
    LastCheckOut: string | null
    ShiftName: string
    ShiftStartTime: string
    ShiftEndTime: string
    Latitude: number
    Longitude: number
    WorkedHours: string
    BreakHours: string
    Status: string
    Exceptions: string | null
    ExceptionMessage: string
}

const avatarColors = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#FAEEDA', color: '#854F0B' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FBEAF0', color: '#993556' },
]

const getInitials = (name: string) => name?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()

const StatusBadge: React.FC<{ status: AttendanceRecord['Status'] }> = ({ status }) => {
    const map: Record<string, { label: string; bg: string; color: string; dot: string }> = {
        present: { label: 'Present', bg: '#EAF3DE', color: '#3B6D11', dot: '#639922' },
        clockedin: { label: 'ClockedIn', bg: '#EAF3DE', color: '#3B6D11', dot: '#639922' },
        clockedout: { label: 'ClockedOut', bg: '#FCEBEB', color: '#A32D2D', dot: '#E24B4A' },
        onbreak: { label: 'On Break', bg: '#FAEEDA', color: '#bd6e0d', dot: '#EF9F27' },
        absent: { label: 'Absent', bg: '#FCEBEB', color: '#A32D2D', dot: '#E24B4A' },
        'short day': { label: 'Short Day', bg: '#FAEEDA', color: '#854F0B', dot: '#EF9F27' },
        'half day': { label: 'Half Day', bg: '#E6F8F4', color: '#3CBA9F', dot: '#2A9D84' },
        weekend: { label: 'Weekend', bg: '#F1EFE8', color: '#5F5E5A', dot: '#888780' },
        'non working day': { label: 'Non Working Day', bg: '#E6F1FB', color: '#185FA5', dot: '#378ADD' },
        'custom': { label: 'Custom', bg: '#F1EFE8', color: '#5F5E5A', dot: '#888780' },
        holiday: { label: 'Holiday', bg: '#E6F1FB', color: '#185FA5', dot: '#378ADD' },
        partial_day: { label: 'Partial Day', bg: '#FAEEDA', color: '#854F0B', dot: '#EF9F27' },
    }

    const s = map[status.toLowerCase()]
    return (
        <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s?.bg, color: s?.color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s?.dot, display: 'inline-block', flexShrink: 0 }} />
                {s?.label}
            </span>
        </>
    )
}

const SourceCell: React.FC<{ source: AttendanceRecord['SourceName'] }> = ({ source }) => {
    if (!source) return <span style={{ color: '#ADB5BD' }}>—</span>

    const sources = source.split(',').map(s => s.trim()).filter(Boolean)
    const first = sources[0]
    const extra = sources.length - 1
    const key = first.toLowerCase()

    return (
        <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6C757D' }}
            data-tooltip-id={extra > 0 ? 'tooltip-description' : undefined}
            data-tooltip-content={extra > 0 ? sources.join(', ') : undefined}
        >
            {key === 'web' ? <i className="fa-solid fa-desktop" /> : key === 'mobile' ? <i className="fa-solid fa-mobile-screen-button" /> : key === 'biometric' ? <i className="fa-solid fa-fingerprint" /> : null}
            {first.charAt(0).toUpperCase() + first.slice(1)}
            {extra > 0 && <span style={{ fontWeight: 600 }}>+{extra}</span>}
        </span>
    )
}
// const SourceCell: React.FC<{ source: AttendanceRecord['SourceName'] }> = ({ source }) => {
//     if (!source) return <span style={{ color: '#ADB5BD' }}>—</span>
//     return (
//         <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6C757D' }}>
//             {source.toLowerCase() === 'web' ? <i className="fa-solid fa-desktop"></i> : source.toLowerCase() === 'mobile' ? <i className="fa-solid fa-mobile-screen-button"></i> : source.toLowerCase() === 'biometric' ? <i className="fa-solid fa-fingerprint"></i> : ''}
//             {source.charAt(0).toUpperCase() + source.slice(1)}
//         </span>
//     )
// }

const ACTIONS_COL_WIDTH = 120

const AttendanceTable = ({ filters, isBreakMenuOpen }: any) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { cultureInfoName } = useAppSelector((state) => state.globalState);
    const { AccountEmployeeAttendanceList, isLoading, isReload, pageNo, pageSize } = useAppSelector((state) => state.attendance);
    const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
    const [showGeofenceModal, setShowGeofenceModal] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(['employee', 'date', 'firstCheckIn', 'lastCheckOut', 'shiftName', 'workedHours', 'breakHours', 'status', 'exceptionType', 'attendanceSource',])
    const AccountEmployeeId = localStorage.getItem('accountEmployeeId');
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const scrollPositionRef = useRef(0)

    const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
        scrollPositionRef.current = e.currentTarget.scrollTop
    }

    useLayoutEffect(() => {
        if (!isLoading && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current
        }
    }, [isLoading, AccountEmployeeAttendanceList])

    const STICKY_RIGHT_STYLE = {
        position: 'sticky' as const,
        right: 0,
        background: '#F8F9FA',
        borderLeft: '1px solid #E9ECEF',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.05)'
    };

    const STICKY_BODY_STYLE = {
        position: 'sticky' as const,
        right: 0,
        // zIndex: 1,
         zIndex: isBreakMenuOpen ? 0 : 1,
        background: '#fff',
        borderLeft: '1px solid #E9ECEF',
        borderBottom: '1px solid #E9ECEF',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.05)'
    };
    useEffect(() => {
        const payload = {
            AccountEmployeeId,
            StartDate: filters?.StartDate ?? moment().format('YYYY-MM-DD'),
            EndDate: filters?.EndDate ?? moment().format('YYYY-MM-DD'),
            searchEmployeeId: filters?.searchEmployeeIds,
            accountDepartmentId: filters?.accountDepartmentIds,
            accountLocationId: filters?.accountLocationIds,
            status: filters?.statusIds,
            employeeTypeId: filters?.employeeTypeIds,
            attendanceSourceId: filters?.attendanceSourceIds,
            employeeStatus: filters?.employeeStatus ?? 0,
            pageNumber: pageNo,
            pageSize: pageSize,
        }
        dispatch(GetAccountEmployeeAttendanceListAsync(payload));
    }, [isReload, pageNo, pageSize, filters])

    const formatHoursMinutes = (time: any) => {
        if (!time) return '0hr 0m'
        const parts = time.split(':')
        const hours = parseInt(parts[0], 10) || 0
        const minutes = parseInt(parts[1], 10) || 0
        return `${hours}hr ${minutes}m`
    }

    const columns = useMemo<ColumnDef<AttendanceRecord>[]>(() => [
        {
            id: 'employee',
            accessorKey: 'Employeename',
            header: () => getValueFromResource('Employee', dispatch, t),
            cell: ({ row }) => {
                const c = avatarColors[parseInt(row.id) % avatarColors.length]
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: c.bg, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                            {getInitials(row.original.Employeename)}
                        </div>
                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#212529', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.original?.Employeename}
                            </div>
                            <div style={{ fontSize: 11, color: '#ADB5BD', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.original.EmployeeCode}
                            </div>
                        </div>
                    </div>
                )
            },
            enableSorting: true,
            size: 150,
            minSize: 100,
            maxSize: 400,
        },
        {
            id: 'date',
            accessorKey: 'AttendanceDate',
            header: () => getValueFromResource('Date', dispatch, t),
            cell: ({ row }) => <span style={{ fontSize: 12, color: '#212529', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{formatDateWithDateAndMonthName(row.original?.AttendanceDate, cultureInfoName.toString())}</span>,
            enableSorting: true,
            size: 100,
            minSize: 90,
            maxSize: 200
        },
        {
            id: 'firstCheckIn',
            accessorKey: 'FirstCheckIn',
            header: () => getValueFromResource('Clock-In', dispatch, t),
            cell: ({ row }) => {
                const val = row.original.FirstCheckIn
                return <span style={{ fontSize: 12, color: '#212529', fontWeight: 400 }}>{val ? format12HoursOnly(val, 0) : '—'}</span>
            },
            enableSorting: true,
            size: 100, minSize: 90, maxSize: 180,
        },
        {
            id: 'lastCheckOut',
            accessorKey: 'LastCheckOut',
            header: () => getValueFromResource('Clock-Out', dispatch, t),
            cell: ({ row }) => {
                const val = row.original.LastCheckOut
                const isEarly = val === '01:10 PM'
                return <span style={{ fontSize: 12, color: '#212529', fontWeight: isEarly ? 500 : 400 }}>{val ? format12HoursOnly(val, 0) : '—'}</span>
            },
            enableSorting: true,
            size: 100, minSize: 90, maxSize: 180,
        },
        {
            id: 'shiftName',
            accessorKey: 'ShiftName',
            header: () => getValueFromResource('Shift', dispatch, t),
            cell: ({ row }) => {
                return (
                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <a className='text-dark fw-bold d-block' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row?.original?.ShiftName}
                        </a>
                        <span style={{ color: '#7b8ba6', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                            {`${format12HoursOnly(row?.original?.ShiftStartTime, 0)} - ${format12HoursOnly(row?.original?.ShiftEndTime, 0)}`}
                        </span>
                    </div>
                )
            },
            enableSorting: false,
            size: 130, minSize: 110, maxSize: 260,
        },
        {
            id: 'workedHours',
            accessorKey: 'WorkedHours',
            header: () => getValueFromResource('Worked Hours', dispatch, t),
            cell: ({ row }) => <span style={{ fontSize: 12, color: '#212529' }}>{formatHoursMinutes(row.original.WorkedHours)}</span>,
            enableSorting: true,
            size: 120, minSize: 100, maxSize: 180,
        },
        {
            id: 'breakHours',
            accessorKey: 'BreakHours',
            header: () => getValueFromResource('Break Hours', dispatch, t),
            cell: ({ row }) => <span style={{ fontSize: 12, color: '#212529' }}>{formatHoursMinutes(row?.original?.BreakHours)}</span>,
            enableSorting: false,
            size: 120, minSize: 90, maxSize: 180,
        },
        {
            id: 'status',
            accessorKey: 'Status',
            header: () => getValueFromResource('Status', dispatch, t),
            cell: ({ row }) => <StatusBadge status={row.original.Status} />,
            enableSorting: true,
            size: 130, minSize: 80, maxSize: 180,
        },
        {
            id: 'exceptionType',
            accessorKey: 'Exceptions',
            header: () => getValueFromResource('Exceptions', dispatch, t),
            cell: ({ row }) => {
                const exceptions = row.original.Exceptions
                const exceptionMessages = row.original?.ExceptionMessage

                if (!exceptions) {
                    return <span>—</span>
                }

                const exceptionList = exceptions.split(',').map(e => e.trim()).filter(Boolean)
                const messageList = exceptionMessages
                    ? exceptionMessages.split(',').map(m => m.trim()).filter(Boolean)
                    : []

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {exceptionList.map((exception, idx) => (
                            <span
                                key={idx}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    background: '#FAEEDA',
                                    color: '#854F0B',
                                    borderRadius: 20,
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                }}
                                data-tooltip-id="tooltip-description"
                                data-tooltip-content={getValueFromResource(messageList[idx] ?? exception, dispatch, t)}
                            >
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF9F27', display: 'inline-block', flexShrink: 0 }} />
                                {exception}
                            </span>
                        ))}
                    </div>
                )
            },
            enableSorting: false,
            size: 200, minSize: 180, maxSize: 450,
        },
        {
            id: 'attendanceSource',
            accessorKey: 'SourceName',
            header: () => getValueFromResource('Source', dispatch, t),
            cell: ({ row }) => <SourceCell source={row.original.SourceName} />,
            enableSorting: false,
            size: 100, minSize: 90, maxSize: 160,
        },
    ], [dispatch, t, cultureInfoName])

    const table = useReactTable({
        data: AccountEmployeeAttendanceList,
        columns,
        state: { sorting, columnOrder, columnSizing },
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        onColumnOrderChange: setColumnOrder,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        columnResizeMode: 'onChange',
        defaultColumn: {
            size: 150,
            minSize: 80,
            maxSize: 500,
        },
    })

    const totalSize = table.getTotalSize() + ACTIONS_COL_WIDTH
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return
        const newOrder = [...columnOrder]
        const [removed] = newOrder.splice(result.source.index, 1)
        newOrder.splice(result.destination.index, 0, removed)
        setColumnOrder(newOrder)
    }

    const headerGroup = table.getHeaderGroups()[0]

    const onClose = () => {
        setShowAttendanceDetails(false);
    }

    const onCloseModal = () => {
        setShowGeofenceModal(false);
    }

    const TOOLTIP_STYLE = {
        fontSize: 12, fontWeight: 'normal', padding: 8, borderRadius: 6,
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
        backgroundColor: 'black', color: 'white', zIndex: 999999,
        maxWidth: 220, whiteSpace: 'normal' as const,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {isLoading && <Loader />}
            <div className='mt-2' style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <div ref={scrollContainerRef} onScroll={handleTableScroll} style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid #E9ECEF', borderRadius: 8, width: '100%', position: 'relative', isolation: 'isolate' }}>
                    <table style={{ width: '100%', minWidth: totalSize, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                        <colgroup>
                            {headerGroup.headers.map(header => (
                                <col key={header.id} style={{ width: header.getSize() }} />
                            ))}
                            <col style={{ width: ACTIONS_COL_WIDTH }} />
                        </colgroup>
                        <thead style={{ background: '#F8F9FA' }}>
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="columns" direction="horizontal">
                                    {provided => (
                                        <tr ref={provided.innerRef} {...provided.droppableProps}>
                                            {headerGroup.headers.map((header, index) => (
                                                <Draggable key={header.id} draggableId={header.id} index={index}>
                                                    {(prov, snapshot) => (
                                                        <th
                                                            ref={prov.innerRef}
                                                            {...prov.draggableProps}
                                                            style={{
                                                                ...prov.draggableProps.style,
                                                                position: 'sticky',
                                                                top: 0,
                                                                // zIndex: 2,
                                                                padding: '10px 12px',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: '#343A40',
                                                                borderBottom: '1px solid #E9ECEF',
                                                                borderRight: '1px solid #E9ECEF',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                background: snapshot.isDragging ? '#EEF0FD' : '#F8F9FA',
                                                                opacity: snapshot.isDragging ? 0.85 : 1,
                                                                boxShadow: snapshot.isDragging ? '0 2px 8px rgba(67,97,238,0.15)' : 'none',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                                                <span
                                                                    {...prov.dragHandleProps}
                                                                    style={{ cursor: 'grab', color: '#C4C4C4', display: 'flex', alignItems: 'center', marginRight: 2, flexShrink: 0 }}
                                                                    title="Drag to reorder"
                                                                >
                                                                    <i className="fa fa-grip-vertical six-dots-icon" style={{ fontSize: "12px", color: "#6B7280", marginRight: '3px', opacity: 0.7 }}></i>
                                                                </span>

                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                                </span>

                                                                {header.column.getCanSort() && (
                                                                    <span
                                                                        onClick={header.column.getToggleSortingHandler()}
                                                                        style={{ display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer', marginLeft: 2, flexShrink: 0 }}
                                                                    >
                                                                        <span style={{ width: 0, height: 0, borderLeft: '3.5px solid transparent', borderRight: '3.5px solid transparent', borderBottom: `4px solid ${header.column.getIsSorted() === 'asc' ? '#4361EE' : '#C4C4C4'}` }} />
                                                                        <span style={{ width: 0, height: 0, borderLeft: '3.5px solid transparent', borderRight: '3.5px solid transparent', borderTop: `4px solid ${header.column.getIsSorted() === 'desc' ? '#4361EE' : '#C4C4C4'}` }} />
                                                                    </span>
                                                                )}
                                                                {header.column.getCanResize() && (
                                                                    <div
                                                                        onMouseDown={header.getResizeHandler()}
                                                                        onTouchStart={header.getResizeHandler()}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            right: 0,
                                                                            top: 0,
                                                                            height: '100%',
                                                                            width: 5,
                                                                            cursor: 'col-resize',
                                                                            userSelect: 'none',
                                                                            touchAction: 'none',
                                                                            background: header.column.getIsResizing() ? '#4361EE' : 'transparent',
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </th>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            <th style={{ ...STICKY_RIGHT_STYLE, position: 'sticky', top: 0, right: 0, zIndex: isBreakMenuOpen ? 0 : 2, padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#343A40', borderBottom: '1px solid #E9ECEF', background: '#F8F9FA' }}>
                                                {getValueFromResource('Actions', dispatch, t)}
                                            </th>
                                        </tr>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    style={{ background: '#fff' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FF')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} style={{ padding: '10px 12px', borderRight: '1px solid #E9ECEF', borderBottom: '1px solid #E9ECEF', verticalAlign: 'middle', width: cell.column.getSize(), overflow: 'hidden' }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                    <td style={{ ...STICKY_BODY_STYLE, padding: '10px 12px', verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <button
                                                className='mb-1'
                                                onClick={() => {
                                                    const payload = {
                                                        AccountEmployeeId: row?.original?.AccountEmployeeId,
                                                        AttendanceDate: moment(row?.original?.AttendanceDate).format('YYYY-MM-DD')
                                                    }
                                                    dispatch(GetAccountEmployeeAttendanceViewDetails(payload))
                                                    setShowAttendanceDetails(true)
                                                }}
                                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#ADB5BD', borderRadius: 4 }}
                                                aria-label={getValueFromResource('View details', dispatch, t)}
                                            >
                                                <i className="bi bi-eye-fill text-gray"
                                                    data-tooltip-id="tooltip-description"
                                                    data-tooltip-content={getValueFromResource('View details', dispatch, t)}
                                                />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const payload = {
                                                        AccountEmployeeId: row?.original?.AccountEmployeeId,
                                                        AttendanceDate: moment(row?.original?.AttendanceDate).format('YYYY-MM-DD')
                                                    }
                                                    dispatch(GetAttendanceGeofenceLocationActivity(payload))
                                                    setShowGeofenceModal(true)
                                                }}
                                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#ADB5BD', borderRadius: 4 }}
                                                aria-label={getValueFromResource('Locations', dispatch, t)}
                                            >
                                                <i
                                                    className="ki-duotone ki-geolocation fs-5"
                                                    data-tooltip-id="tooltip-description"
                                                    data-tooltip-content={getValueFromResource('Locations', dispatch, t)}
                                                >
                                                    <span className="path1" /><span className="path2" />
                                                </i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!isLoading && table.getRowModel().rows.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length + 1} className='text-center text-muted py-4' style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                        <p className='noDataFound'>
                                            {getValueFromResource('No Record Found', dispatch, t)}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {showAttendanceDetails && <AttendanceDetail onClose={onClose} />}
                {showGeofenceModal && <GeofenceModal onCloseModal={onCloseModal} />}
                <ReactTooltip id="tooltip-description" style={TOOLTIP_STYLE} place="top" delayShow={100} delayHide={100} clickable />
            </div>
        </div>
    )
}

export default AttendanceTable